import { Box, Stack, Typography, Skeleton } from "@mui/material";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip } from "chart.js";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ProductionCard = ({ data, color, loading }) => {
	if (loading) {
		return (
			<Box
				sx={{
					flex: 1,
					p: "16px 18px",
					backgroundColor: "background.paper",
					border: "1px solid rgba(29,84,162,0.1)",
					borderRadius: "12px",
					boxShadow: "0 1px 8px rgba(29,84,162,0.06)",
				}}
			>
				<Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
					<Stack direction={"row"} alignItems={"center"} gap={1} ml={0.5}>
						<Skeleton variant="circular" width={24} height={24} />
						<Skeleton variant="text" width={120} height={20} />
					</Stack>
					<Skeleton variant="rounded" width={48} height={18} />
				</Stack>
				<Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mb: 1.75 }}>
					{[0, 1, 2].map((i) => (
						<Box key={i} sx={{ backgroundColor: "background.default", borderRadius: 1.5, p: "8px 10px" }}>
							<Skeleton variant="text" width="60%" height={12} />
							<Skeleton variant="text" width="40%" height={18} />
						</Box>
					))}
				</Box>
				<Skeleton variant="rounded" width="100%" height={110} />
			</Box>
		);
	}

	const hasData = data && typeof data === "object" && Object.keys(data).length > 0;
	const currentPeriod = new Date().toISOString().slice(0, 7);
	const effectiveData = hasData ? data : { [currentPeriod]: 0 };

	const entries = Object.entries(effectiveData).sort(([a], [b]) => a.localeCompare(b));

	const labels = entries.map(([period]) => {
		const [, month] = period.split("-");
		return MONTHS[Number(month) - 1];
	});

	const values = entries.map(([, value]) => value);
	const chartValues = values.length === 1 ? [values[0], values[0]] : values;
	const chartLabels = labels.length === 1 ? [labels[0], labels[0]] : labels;

	const total = values.reduce((a, b) => a + b, 0);
	const avg = values.length ? Math.round(total / values.length) : 0;
	const peak = values.length ? Math.max(...values) : 0;
	const low = values.length ? Math.min(...values) : 0;

	const prev = values.slice(0, -1);
	const prevTotal = prev.reduce((a, b) => a + b, 0);
	const change = prevTotal ? (((total - prevTotal) / prevTotal) * 100).toFixed(1) : null;
	const isUp = Number(change) >= 0;

	const chartData = {
		labels: chartLabels,
		datasets: [
			{
				data: chartValues,
				borderColor: color,
				backgroundColor: color + "10",
				fill: true,
				tension: 0.4,
				borderWidth: 2,
				pointRadius: 2,
				pointHoverRadius: 4,
				pointBackgroundColor: color,
			},
		],
	};

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				backgroundColor: "#fff",
				titleColor: "#444",
				bodyColor: "#444",
				borderColor: "#e0e0e0",
				borderWidth: 0.5,
				padding: 8,
				callbacks: { label: (ctx) => " " + ctx.parsed.y.toLocaleString() },
			},
		},
		scales: {
			x: {
				grid: { display: false },
				border: { display: false },
				ticks: { font: { size: 10 }, color: "#888", maxRotation: 0 },
			},
			y: { display: false },
		},
	};

	return (
		<Box
			sx={{
				flex: 1,
				p: "16px 18px",
				backgroundColor: "background.paper",
				border: "1px solid rgba(29,84,162,0.1)",
				borderRadius: "12px",
				boxShadow: "0 1px 8px rgba(29,84,162,0.06)",
			}}
		>
			{/* Header */}
			<Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
				<Stack direction={"row"} alignItems={"center"} gap={1} ml={0.5}>
					<PeopleAltIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: "#1D54A2" }} />
					<Typography variant="subtitle2" fontWeight={500} letterSpacing={1}>
						<strong>{total.toLocaleString()}</strong> total members
					</Typography>
				</Stack>
				{change !== null && (
					<Box
						sx={{
							fontSize: "0.68rem",
							fontWeight: 500,
							px: 1,
							py: 0.25,
							borderRadius: "20px",
							backgroundColor: isUp ? "#EAF3DE" : "#FCEBEB",
							color: isUp ? "#3B6D11" : "#A32D2D",
						}}
					>
						{isUp ? "+" : ""}
						{change}%
					</Box>
				)}
			</Stack>

			{/* Metrics */}
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(3, 1fr)",
					gap: 1,
					mb: 1.75,
				}}
			>
				{[
					{ label: "Avg / mo", value: avg.toLocaleString() },
					{ label: "Peak", value: peak.toLocaleString() },
					{ label: "Low", value: low.toLocaleString() },
				].map(({ label, value }) => (
					<Box key={label} sx={{ backgroundColor: "background.default", borderRadius: 1.5, p: "8px 10px" }}>
						<Typography
							sx={{ fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "text.secondary", mb: 0.25 }}
						>
							{label}
						</Typography>
						<Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>{value}</Typography>
					</Box>
				))}
			</Box>

			{/* Chart */}
			<Box sx={{ height: 110 }}>
				<Line data={chartData} options={chartOptions} />
			</Box>

			{/* Legend */}
			<Stack direction="row" gap={2} mt={1}>
				<Stack direction="row" alignItems="center" gap={0.5}>
					<Box sx={{ width: 10, height: 2, backgroundColor: color, borderRadius: 1 }} />
					<Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>Monthly</Typography>
				</Stack>
			</Stack>
		</Box>
	);
};

export default ProductionCard;
